import React, { useMemo, useState } from 'react';

/**
 * "Show the money" ROI calculator for the Funeral Professional
 * program page.
 *
 * Director dials in two inputs — obituaries handled per month + the
 * loaded hourly cost of the staff that would otherwise write them —
 * and the widget renders a simple, side-by-side yearly cost
 * comparison:
 *
 *   By hand             █████████████████████████████████  $X
 *   With ObitAuthor     ██▓▓                              $Y
 *                         ^labor   ^credit    ← Savings: $X−Y
 *
 * Plain horizontal bars instead of an area chart because directors
 * told us the dual-area visualization was hard to read and the tool
 * cost looked like a "negative" value. Bars map directly to real
 * dollar totals with no scale trickery: the SAME pixel = the SAME
 * dollar on both bars.
 *
 * No runtime deps — everything is CSS flex + percent widths. Numbers
 * update live on slider change.
 */

// Per-obit pricing bracket (display-only). Pulled from the CREDIT_PACKAGES
// config in fdRoutes on the backend, rounded for the sticker claim.
function pricePerObit(monthly) {
  // Match what the landing page promises: "75¢ to $1 per obituary".
  if (monthly >= 250) return 0.75;
  if (monthly >= 80) return 0.85;
  if (monthly >= 30) return 0.90;
  return 1.00;
}

const HAND_MIN_PER_OBIT = 30;
const TOOL_MIN_PER_OBIT = 5;

const FdRoiCalc = () => {
  const [obitsPerMonth, setObitsPerMonth] = useState(40);
  const [hourlyRate, setHourlyRate] = useState(35);

  const data = useMemo(() => {
    const price = pricePerObit(obitsPerMonth);
    const obitsPerYear = obitsPerMonth * 12;

    // Yearly totals (plain, no seasonal noise — users were double-
    // taking at "why is December more expensive").
    const handLaborHrs = (obitsPerYear * HAND_MIN_PER_OBIT) / 60;
    const toolLaborHrs = (obitsPerYear * TOOL_MIN_PER_OBIT) / 60;

    const handCostYr = handLaborHrs * hourlyRate;         // all labor
    const toolLaborYr = toolLaborHrs * hourlyRate;        // labor only
    const toolCreditYr = obitsPerYear * price;            // ObitAuthor spend
    const withToolYr = toolLaborYr + toolCreditYr;

    const savedHoursYr = handLaborHrs - toolLaborHrs;
    const netSavingsYr = handCostYr - withToolYr;
    const netSavingsMo = netSavingsYr / 12;

    // Bar widths — same scale on both rows so the visual is honest.
    // Same pixel = same dollar, so directors can compare at a glance.
    const scaleMax = Math.max(handCostYr, withToolYr, 1) * 1.04;
    const pct = (v) => `${Math.max(0, Math.min(100, (v / scaleMax) * 100))}%`;
    const handWidth = pct(handCostYr);
    const toolTotalWidth = pct(withToolYr);
    const savingsWidth = pct(netSavingsYr);
    // Inner split of the "With ObitAuthor" bar — these are percentages
    // of the parent bar, not of the whole track.
    const toolShare = withToolYr > 0 ? toolLaborYr / withToolYr : 0;
    const toolLaborInner = `${(toolShare * 100).toFixed(1)}%`;
    const toolCreditInner = `${((1 - toolShare) * 100).toFixed(1)}%`;

    return {
      price,
      handCostYr, toolLaborYr, toolCreditYr, withToolYr,
      savedHoursYr, netSavingsYr, netSavingsMo,
      handWidth, toolTotalWidth, savingsWidth,
      toolLaborInner, toolCreditInner,
    };
  }, [obitsPerMonth, hourlyRate]);

  const fmtMoney = (n) => '$' + Math.round(n).toLocaleString();
  const fmtHours = (n) => Math.round(n).toLocaleString() + ' hrs';

  return (
    <div className="fd-roi-card" role="region" aria-label="ROI calculator for funeral directors">
      <div className="fd-roi-head">
        <div>
          <h3 className="fd-roi-title">What ObitAuthor saves your firm</h3>
          <p className="fd-roi-sub">
            Drag the sliders. Math assumes <strong>30&nbsp;minutes saved per obituary</strong>
            {' '}at {fmtMoney(hourlyRate)}/hr of loaded staff time, minus{' '}
            {data.price === 1 ? '$1' : '$' + data.price.toFixed(2)}&nbsp;per&nbsp;obit in ObitAuthor credits at your volume.
          </p>
        </div>
        <div className="fd-roi-big">
          <span className="fd-roi-big-label">Est. net savings / yr</span>
          <span className="fd-roi-big-value">{fmtMoney(data.netSavingsYr)}</span>
          <span className="fd-roi-big-sub">
            about {fmtMoney(data.netSavingsMo)}/month, after ObitAuthor cost
          </span>
        </div>
      </div>

      <div className="fd-roi-controls">
        <label className="fd-roi-ctrl">
          <span className="fd-roi-ctrl-label">
            Obituaries per month
            <strong>{obitsPerMonth.toLocaleString()}</strong>
          </span>
          <input
            type="range"
            min="5" max="400" step="5"
            value={obitsPerMonth}
            onChange={(e) => setObitsPerMonth(parseInt(e.target.value, 10))}
            className="fd-roi-range"
            aria-label="Obituaries per month"
          />
          <span className="fd-roi-ctrl-scale">
            <span>5</span><span>100</span><span>200</span><span>400</span>
          </span>
        </label>
        <label className="fd-roi-ctrl">
          <span className="fd-roi-ctrl-label">
            Loaded staff rate
            <strong>{fmtMoney(hourlyRate)}/hr</strong>
          </span>
          <input
            type="range"
            min="20" max="80" step="1"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(parseInt(e.target.value, 10))}
            className="fd-roi-range"
            aria-label="Hourly rate"
          />
          <span className="fd-roi-ctrl-scale">
            <span>$20</span><span>$40</span><span>$60</span><span>$80</span>
          </span>
        </label>
      </div>

      {/* ----------------------------------------------------------------
          Yearly cost comparison. Both rows share the same pixel-to-dollar
          scale so the visual is literal: longer bar = more money. The
          "with ObitAuthor" row is a stacked bar: staff time + credits.
         ---------------------------------------------------------------- */}
      <div className="fd-roi-bars" aria-label="Yearly cost comparison">
        <div className="fd-roi-bar-row">
          <div className="fd-roi-bar-side">
            <span className="fd-roi-bar-title">Writing by hand</span>
            <span className="fd-roi-bar-sub">all staff time</span>
          </div>
          <div className="fd-roi-bar-track">
            <div
              className="fd-roi-bar fd-roi-bar-hand"
              style={{ width: data.handWidth }}
            >
              <span className="fd-roi-bar-amount">{fmtMoney(data.handCostYr)}</span>
            </div>
          </div>
        </div>

        <div className="fd-roi-bar-row">
          <div className="fd-roi-bar-side">
            <span className="fd-roi-bar-title">With ObitAuthor</span>
            <span className="fd-roi-bar-sub">staff time + credits</span>
          </div>
          <div className="fd-roi-bar-track">
            <div
              className="fd-roi-bar fd-roi-bar-tool"
              style={{ width: data.toolTotalWidth }}
            >
              <span
                className="fd-roi-bar-seg fd-roi-bar-seg-labor"
                style={{ width: data.toolLaborInner }}
                title={`Staff time: ${fmtMoney(data.toolLaborYr)}/yr`}
              />
              <span
                className="fd-roi-bar-seg fd-roi-bar-seg-credit"
                style={{ width: data.toolCreditInner }}
                title={`ObitAuthor credits: ${fmtMoney(data.toolCreditYr)}/yr`}
              />
              <span className="fd-roi-bar-amount">
                {fmtMoney(data.withToolYr)}
              </span>
            </div>
          </div>
        </div>

        <div className="fd-roi-bar-row fd-roi-bar-row-savings">
          <div className="fd-roi-bar-side">
            <span className="fd-roi-bar-title">Your savings</span>
            <span className="fd-roi-bar-sub">per year</span>
          </div>
          <div className="fd-roi-bar-track">
            <div
              className="fd-roi-bar fd-roi-bar-savings"
              style={{ width: data.savingsWidth }}
            >
              <span className="fd-roi-bar-amount">
                {fmtMoney(data.netSavingsYr)}
              </span>
            </div>
          </div>
        </div>

        <div className="fd-roi-legend fd-roi-legend-bars" aria-hidden="true">
          <span className="fd-roi-legend-item fd-roi-legend-hand">
            <span className="fd-roi-swatch" /> Staff time by hand
          </span>
          <span className="fd-roi-legend-item fd-roi-legend-tool">
            <span className="fd-roi-swatch" /> Staff time with ObitAuthor
          </span>
          <span className="fd-roi-legend-item fd-roi-legend-credit">
            <span className="fd-roi-swatch" /> ObitAuthor credits
          </span>
          <span className="fd-roi-legend-item fd-roi-legend-save">
            <span className="fd-roi-swatch" /> Savings
          </span>
        </div>
      </div>

      <div className="fd-roi-breakdown" role="list">
        <div className="fd-roi-breakdown-item" role="listitem">
          <span className="fd-roi-breakdown-label">Staff hours saved / yr</span>
          <span className="fd-roi-breakdown-value">{fmtHours(data.savedHoursYr)}</span>
        </div>
        <div className="fd-roi-breakdown-item" role="listitem">
          <span className="fd-roi-breakdown-label">By hand / yr</span>
          <span className="fd-roi-breakdown-value">{fmtMoney(data.handCostYr)}</span>
        </div>
        <div className="fd-roi-breakdown-item" role="listitem">
          <span className="fd-roi-breakdown-label">With ObitAuthor / yr</span>
          <span className="fd-roi-breakdown-value">{fmtMoney(data.withToolYr)}</span>
        </div>
        <div className="fd-roi-breakdown-item fd-roi-breakdown-net" role="listitem">
          <span className="fd-roi-breakdown-label">Net savings / yr</span>
          <span className="fd-roi-breakdown-value">{fmtMoney(data.netSavingsYr)}</span>
        </div>
      </div>
    </div>
  );
};

export default FdRoiCalc;
